import { Accordion, Alert, Box, Center, Divider, Group, Stack, Text } from '@mantine/core';
import StatusIndicator from '@/components/StatusIndicator.tsx';
import React, { useState } from 'react';
import useRetrieveExtractEntityFields from '@/hooks/api/extractExecutions/useRetrieveExtractEntityFields.ts';
import CustomSpinner from '@/components/Spinner.tsx';
import ExtractEntityFieldRenderer from '@/pages/Run/components/ExtractResults/ExtractEntityFieldRenderer.tsx';
import StepExecutionLogs from '@/pages/Run/components/StepExecutionLogs/StepExecutionLogs.tsx';
import RerunMenu from '@/components/RerunMenu.tsx';
import { useHover } from '@mantine/hooks';
import RunInlineEmptyState from '@/pages/Run/components/RunInlineEmptyState.tsx';
import { Run } from '@/types/runs.ts';
import { StepRun } from '@/types/stepExecutions.ts';
import { ExtractEntity } from '@/types/extract.ts';
import CachedExecutionIndicator from '@/pages/Run/components/CachedExecutionIndicator.tsx';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';

type props = {
  extractEntity: ExtractEntity;
  stepRun?: StepRun;
  run: Run;
  isDataViewer: boolean;
  defaultShowChildren: boolean;
};

const DEFAULT_ENTITY_NAME = '__default_entity__';

const ExtractEntityRenderer = ({
  extractEntity,
  run,
  stepRun,
  isDataViewer,
  defaultShowChildren,
}: props) => {
  const [showChildren, setShowChildren] = useState(defaultShowChildren);

  const { hovered, ref } = useHover();

  const { data: entityFields, isError, isFetching, refetch } = useRetrieveExtractEntityFields(
    showChildren ? extractEntity.id : undefined,
  );

  const noLeftPadding = isDataViewer && extractEntity.identifier === DEFAULT_ENTITY_NAME;
  const fieldResults = entityFields?.results;
  const hasFieldResults = (fieldResults?.length ?? 0) > 0;

  return (
    <Box ref={ref} bg={'white'} style={{ borderRadius: '6px' }}>
      {extractEntity.has_fields ? (
        <Accordion
          value={showChildren ? 'children' : null}
          onChange={(value) => setShowChildren(value === 'children')}
          pl={noLeftPadding ? undefined : 'xs'}
        >
          <Accordion.Item value={'children'} style={{ border: 0 }}>
            <Accordion.Control>
              <Inner
                extractEntity={extractEntity}
                run={run}
                stepRun={stepRun}
                isDataViewer={isDataViewer}
                hovered={hovered}
              />
            </Accordion.Control>
            <Accordion.Panel styles={{ content: { padding: 0 } }} pl={'xs'} pb={'xs'}>
              {!isDataViewer && (
                <StepExecutionLogs
                  // stepExecution={stepExecution}
                  logItemId={extractEntity.id}
                  enabled={showChildren}
                  logsType={'extract_entity_errors'}
                  // entityId={extractEntity.id}
                />
              )}
              {isError ? (
                <Alert color="orange" title="Extracted fields unavailable" role="alert">
                  <Stack gap="xs" align="flex-start">
                    <Text size="sm">The extracted fields could not be loaded.</Text>
                    <StyledButton
                      size="sm"
                      variant="outline"
                      loading={isFetching}
                      onClick={() => void refetch()}
                    >
                      Retry
                    </StyledButton>
                  </Stack>
                </Alert>
              ) : fieldResults === undefined ? (
                <Center mt={'xs'}>
                  <CustomSpinner size={'sm'} />
                </Center>
              ) : hasFieldResults ? (
                <Stack gap={'8px'} my={'5px'}>
                  {fieldResults.map((entityField, idx) => (
                    <Stack key={idx} gap={'8px'}>
                      {idx > 0 && <Divider mb={'0'} />}
                      <ExtractEntityFieldRenderer
                        run={run}
                        stepRun={stepRun}
                        extractEntity={extractEntity}
                        extractEntityField={entityField}
                        isDataViewer={isDataViewer}
                      />
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <RunInlineEmptyState message="No extracted fields" />
              )}
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      ) : (
        <Stack ml={noLeftPadding ? undefined : 'xs'} px={'md'} py={'sm'}>
          <Inner
            extractEntity={extractEntity}
            run={run}
            stepRun={stepRun}
            isDataViewer={isDataViewer}
            hovered={hovered}
          />
        </Stack>
      )}
    </Box>
  );
};

type InnerProps = {
  extractEntity: ExtractEntity;
  stepRun?: StepRun;
  run: Run;
  isDataViewer: boolean;
  hovered: boolean;
};

const Inner = ({ extractEntity, stepRun, run, isDataViewer, hovered }: InnerProps) => {
  return (
    <Group wrap={'nowrap'} mih={'22px'} gap={'xs'}>
      {!isDataViewer && <StatusIndicator status={extractEntity.status} include_text={false} />}
      <Text size="sm">
        <b>
          {extractEntity.identifier === DEFAULT_ENTITY_NAME ? 'Fields' : extractEntity.identifier}
        </b>
      </Text>
      {extractEntity.field_cache_reuse_state === 'all' && (
        <CachedExecutionIndicator label="All fields reused from cache" />
      )}
      <Stack ml={'auto'} mr={0} gap={'4px'} miw={'25px'}>
        {stepRun && (
          <RerunMenu
            run={run}
            stepRun={stepRun}
            rerunAllData={{
              extractEntitiesToRerun: [
                { extract_entity_index: extractEntity.index, fields: 'all' },
              ],
            }}
            rerunFailedData={{
              extractEntitiesToRerun: [
                { extract_entity_index: extractEntity.index, fields: 'failed' },
              ],
            }}
            buttonOrIcon={'icon'}
            showOnHover={true}
            hovered={hovered}
          />
        )}
      </Stack>
    </Group>
  );
};

export default ExtractEntityRenderer;
